import { VehicleWithChauffeurResponse } from '@/types/benefits/VehicleWithChauffeurResponse';
import StyledContainer from '../styled/StyledContainer';
import StyledFormHeaderWithBreadcrumb from '../styled/StyledFormHeaderWithBreadcrumb';
import { formatBenefitCode } from '@/utils/displayCodes';
import StyledButton from '../styled/StyledButton';
import { useLocation, useNavigate } from 'react-router';
import { PATHS } from '@/constants/Paths';
import { buildBenefitsListSearch } from '@/utils/searchParams/buildBenefitsListSearch';
import { getListSearchFromLocation } from '@/utils/searchParams/getListSearchFromLocation';
import ArrowLeftIcon from '@/assets/icons/arrow-left.svg?react';
import EditIcon from '@/assets/icons/edit-01.svg?react';
import CopyIcon from '@/assets/icons/copy-03.svg?react';
import SaveIcon from '@/assets/icons/save-02.svg?react';
import PlusIcon from '@/assets/icons/plus.svg?react';
import { useMemo, useState } from 'react';
import HorizontalLine from '../ui/HorizontalLine';
import {
  FormProvider,
  SubmitHandler,
  useForm,
  useWatch,
} from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  useDuplicateVehicleWithChauffeur,
  useUpdateVehicleWithChauffeur,
} from '@/hooks/useVehicleWithChauffeur';
import { SupplierStatus } from '@/types/suppliers/SupplierStatus';
import clsx from 'clsx';
import ModalDuplication from '../ui/ModalDuplication';
import BenefitDeletionModal from '@/components/benefits/BenefitDeletionModal';
import StyledButtonLoader from '../styled/StyledButtonLoader';
import { getFileKeyFromUrl } from '@/utils/s3/getFileKeyFromUrl';
import { VEHICLE_WITH_CHAUFFEUR_DUPLICATION_FIELDS } from '@/constants/benefitFields';
import BenefitTypeCell from '../benefits/BenefitTypeCell';
import { useToast } from '@/hooks/useToast';
import { useTranslation } from '@/i18n';
import { useTranslatedConst } from '@/hooks/useTranslatedConst';
import { SupportedLocale } from '@/types/supported-locale.type';
import {
  VehicleWithChauffeurFormDataWithTranslations,
  VehicleWithChauffeurSchemaWithTranslations,
} from '@/i18n/translation-type-utils';
import { loadVehicleWithChauffeurDefaultValues } from '@/utils/benefits/vehicles-with-chauffeur/loadVehicleWithChauffeurDefaultValues';
import { useTranslateFields } from '@/hooks/useTranslate';
import LanguageSwitchWithTranslateBtn from '../translation/LanguageSwitchWithTranslateBtn';
import StyledStepper from '../styled/StyledStepper';
import { ArrowRightIcon } from 'lucide-react';
import { GENERAL_BENEFITS_STEPS } from '@/constants/steppers/generalBenefitsSteps';
import BenefitPricingSummary from '../benefits/BenefitPricingSummary';
import VehicleWithChauffeurConfiguration from './VehicleWithChauffeurConfiguration';
import { MarginRateSchema } from '@/types/common/margin-rate.schema';

interface ViewVehicleWithChauffeurFormProps {
  vehicleWithChauffeur: VehicleWithChauffeurResponse;
  suppliers?: {
    id: string;
    name: string;
    currency: string;
  }[];
  language: SupportedLocale;
  setLanguage: React.Dispatch<React.SetStateAction<SupportedLocale>>;
}

type SubmitFormType = (
  isDraft?: boolean,
) => SubmitHandler<VehicleWithChauffeurFormDataWithTranslations>;

const ViewVehicleWithChauffeurForm = ({
  vehicleWithChauffeur,
  suppliers,
  language,
  setLanguage,
}: ViewVehicleWithChauffeurFormProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const getTranslatedConst = useTranslatedConst();
  const [isEditEnabled, setIsEditEnabled] = useState(false);

  const methods = useForm<VehicleWithChauffeurFormDataWithTranslations>({
    resolver: zodResolver(VehicleWithChauffeurSchemaWithTranslations),
    mode: 'onChange',
    defaultValues: loadVehicleWithChauffeurDefaultValues(vehicleWithChauffeur),
  });

  const {
    control,
    getValues,
    setValue,
    handleSubmit,
    reset,
    formState: { isValid, errors },
  } = methods;

  const { mutateAsync: updateVehicleWithChauffeur } =
    useUpdateVehicleWithChauffeur(language);
  const { mutateAsync: duplicateVehicleWithChauffeur } =
    useDuplicateVehicleWithChauffeur();

  const selectedCountry = useWatch({ control, name: 'country' });
  const selectedSupplier = useWatch({
    control,
    name: 'supplier',
    defaultValue: {
      label: vehicleWithChauffeur.benefit.supplier.name,
      value: vehicleWithChauffeur.benefit.supplier.id,
    },
  });
  const currentName = useWatch({
    control,
    name: 'name',
    defaultValue: vehicleWithChauffeur.benefit.name,
  });

  const [isDeletionModalOpen, setIsDeletionModalOpen] = useState(false);
  const [isDuplicationModalOpen, setIsDuplicationModalOpen] = useState(false);

  const errorsLength = useMemo(() => Object.keys(errors).length, [errors]);

  const { translateFields, isTranslating } = useTranslateFields({
    targetLanguage: language,
    control,
    setValue,
    fields: [
      'benefit.name',
      'model',
      'shortDescription',
      'longDescription',
    ] as const,
  });

  const [currStep, setCurrStep] = useState(0);

  const { toastAsync } = useToast();

  const submitForm: SubmitFormType =
    (isDraft = true) =>
    async (data) => {
      const formData = new FormData();
      const addIf = (value: string | Blob | undefined, key: string) => {
        if (value !== undefined) formData.append(key, value);
      };
      if (!isDraft) formData.append('status', SupplierStatus.PUBLISHED);
      addIf(data.name, 'name');
      addIf(data.supplier?.value, 'supplierId');
      addIf(data.currency?.value, 'currency');
      addIf(data.country?.value, 'country');
      const parsedMarginRate = MarginRateSchema.safeParse(data.marginRate);
      if (parsedMarginRate.success && parsedMarginRate.data !== undefined)
        formData.append('marginRate', String(parsedMarginRate.data));
      addIf(data.model, 'model');
      addIf(data.shortDescription, 'shortDescription');
      addIf(data.longDescription, 'longDescription');
      if (data.maxCapacity)
        formData.append('maxCapacity', String(data.maxCapacity));
      if (data.tarifs) formData.append('pricings', JSON.stringify(data.tarifs));
      data.images
        .filter((image) => !(image instanceof Blob))
        .forEach((imageUrl) =>
          formData.append('imageUrls[]', getFileKeyFromUrl(imageUrl as string)),
        );
      data.images
        .filter((image) => image instanceof Blob)
        .forEach((image) => formData.append('files', image));
      formData.append('translations', JSON.stringify(data.translations));
      await toastAsync(
        updateVehicleWithChauffeur({
          benefitId: vehicleWithChauffeur.benefit.id,
          data: formData,
        }),
        { success: 'Prestation mise à jour avec succès' },
      );
      if (!isDraft)
        navigate(
          PATHS.home.benefits.list +
            getListSearchFromLocation(location, buildBenefitsListSearch),
        );
    };

  return (
    <FormProvider {...methods}>
      <StyledContainer>
        <StyledFormHeaderWithBreadcrumb
          title={currentName || vehicleWithChauffeur.benefit.name}
          code={formatBenefitCode(vehicleWithChauffeur.benefit.code)}
          label={<BenefitTypeCell type={vehicleWithChauffeur.benefit.type} />}
          breadcrumb={
            <StyledButton
              variant="link"
              title={t('common.backToList')}
              className="text-sm text-[#3D3D3D] pl-0"
              onClick={() =>
                navigate(
                  PATHS.home.benefits.list +
                    getListSearchFromLocation(
                      location,
                      buildBenefitsListSearch,
                    ),
                )
              }
              icon={<ArrowLeftIcon />}
            />
          }
        >
          <StyledButton
            title={t('common.delete')}
            variant="outlined"
            className="font-semibold gap-1 px-3 py-2 text-gray-800 border-gray-300"
            onClick={() => setIsDeletionModalOpen(true)}
          />
          <StyledButton
            variant="outlined"
            title={t('common.duplicate')}
            className="font-semibold gap-1 px-3 py-2"
            onClick={() => setIsDuplicationModalOpen(true)}
            icon={<CopyIcon className="w-5 h-5 stroke-brand-600" />}
          />
          {!isEditEnabled ? (
            <StyledButton
              title={t('common.edit')}
              onClick={() => setIsEditEnabled(true)}
              className="font-semibold gap-1 px-3 py-2"
              icon={<EditIcon className="w-5 h-5 stroke-white" />}
            />
          ) : (
            <>
              <StyledButton
                title={t('common.cancel')}
                onClick={() => {
                  setIsEditEnabled(false);
                  reset();
                }}
                className="font-semibold gap-1 px-3 py-2"
              />
              <StyledButtonLoader
                title={t('common.save')}
                successTitle={t('common.draftSaved')}
                onClick={
                  vehicleWithChauffeur.benefit.status === SupplierStatus.DRAFT
                    ? () => {
                        setIsEditEnabled(false);
                        submitForm(true)(getValues());
                      }
                    : handleSubmit(submitForm(true))
                }
                disabled={
                  vehicleWithChauffeur.benefit.status === SupplierStatus.DRAFT
                    ? currentName == undefined ||
                      selectedSupplier == undefined ||
                      selectedCountry == undefined
                    : !isValid
                }
                className="font-semibold gap-1 px-3 py-2"
                icon={<SaveIcon className="w-5 h-5" />}
              />
            </>
          )}
        </StyledFormHeaderWithBreadcrumb>
        <StyledStepper
          activeStep={currStep}
          steps={getTranslatedConst(GENERAL_BENEFITS_STEPS)}
          setStep={(step: number) => setCurrStep(step)}
        />
        <HorizontalLine />
        <LanguageSwitchWithTranslateBtn
          translateFields={() =>
            translateFields({ translateToAllLocales: true })
          }
          isTranslating={isTranslating}
          language={language}
          setLanguage={setLanguage}
          hideTranslateBtn={!isEditEnabled}
        />
        {currStep === 0 ? (
          <>
            <VehicleWithChauffeurConfiguration
              control={control}
              suppliers={suppliers}
              isEditEnabled={isEditEnabled}
              language={language}
              translateFields={(fields) =>
                translateFields({ fieldsToTranslate: fields })
              }
              isTranslating={isTranslating}
              errorsLength={errorsLength}
            />
            {isEditEnabled && (
              <div className="flex flex-row-reverse p-6">
                <StyledButtonLoader
                  title={t('common.save')}
                  successTitle={t('common.draftSaved')}
                  onClick={
                    vehicleWithChauffeur.benefit.status === SupplierStatus.DRAFT
                      ? () => {
                          setIsEditEnabled(false);
                          submitForm(true)(getValues());
                        }
                      : handleSubmit(submitForm(true))
                  }
                  disabled={
                    vehicleWithChauffeur.benefit.status === SupplierStatus.DRAFT
                      ? currentName == undefined ||
                        selectedSupplier == undefined ||
                        selectedCountry == undefined
                      : !isValid
                  }
                  className="font-semibold gap-1 px-3 py-2"
                  icon={<SaveIcon className="w-5 h-5" />}
                />
              </div>
            )}
            {vehicleWithChauffeur.benefit.status === SupplierStatus.DRAFT &&
              isEditEnabled && (
                <div className="flex flex-row-reverse p-6">
                  <StyledButtonLoader
                    title={t('vehiclesWithChauffeur.publishVehicle')}
                    className="font-semibold gap-1 px-3 py-2"
                    successTitle={t('common.publishedSuccess')}
                    hasSucceeded={!errorsLength}
                    errorTitle={t('common.checkRequiredFields')}
                    onClick={handleSubmit(submitForm(false))}
                    icon={
                      <PlusIcon
                        className={clsx('w-5 h-5', {
                          'fill-white': isValid,
                          'fill-gray-400': !isValid,
                        })}
                      />
                    }
                  />
                </div>
              )}
            <div className="flex flex-row-reverse px-6 pb-6 md:w-fit md:sticky md:left-full md:bottom-0">
              <StyledButton
                title={t('common.pricingSummary')}
                icon={<ArrowRightIcon className="w-5 h-5" />}
                iconPosition="right"
                className="font-semibold gap-1 px-3 py-2"
                onClick={() => {
                  setCurrStep(1);
                  window.scrollTo(0, 0);
                }}
              />
            </div>
          </>
        ) : (
          <>
            <BenefitPricingSummary control={control} />
            {isEditEnabled && (
              <div className="flex flex-row-reverse px-6 pb-6">
                <StyledButtonLoader
                  title={t('common.save')}
                  successTitle={t('common.draftSaved')}
                  onClick={
                    vehicleWithChauffeur.benefit.status === SupplierStatus.DRAFT
                      ? () => {
                          submitForm(true)(getValues());
                        }
                      : handleSubmit(submitForm(true))
                  }
                  disabled={
                    vehicleWithChauffeur.benefit.status === SupplierStatus.DRAFT
                      ? currentName == undefined ||
                        selectedSupplier == undefined ||
                        selectedCountry == undefined
                      : !isValid
                  }
                  className="font-semibold gap-1 px-3 py-2"
                  icon={<SaveIcon className="w-5 h-5" />}
                />
              </div>
            )}
            {vehicleWithChauffeur.benefit.status === SupplierStatus.DRAFT &&
              isEditEnabled && (
                <div className="flex flex-row-reverse p-6 md:sticky md:bottom-0 z-10 bg-white">
                  <StyledButtonLoader
                    title={t('vehiclesWithChauffeur.publishVehicle')}
                    className="font-semibold gap-1 px-3 py-2"
                    onClick={handleSubmit(submitForm(false))}
                    successTitle={t('common.publishedSuccess')}
                    hasSucceeded={!errorsLength}
                    errorTitle={t('common.checkRequiredFields')}
                    icon={
                      <PlusIcon
                        className={clsx('w-5 h-5', {
                          'fill-white': isValid,
                          'fill-gray-400': !isValid,
                        })}
                      />
                    }
                    iconPosition="right"
                  />
                </div>
              )}
          </>
        )}
        {isDeletionModalOpen && vehicleWithChauffeur.benefit.id && (
          <BenefitDeletionModal
            benefitId={vehicleWithChauffeur.benefit.id}
            closeModal={() => {
              setIsDeletionModalOpen(false);
            }}
            onDeleted={() =>
              navigate(
                PATHS.home.benefits.list +
                  getListSearchFromLocation(location, buildBenefitsListSearch),
              )
            }
          />
        )}
        {isDuplicationModalOpen && (
          <ModalDuplication
            actionOnClick={async (selectedFields) => {
              if (vehicleWithChauffeur.id) {
                const duplicatedVehicleWithChauffeur =
                  await duplicateVehicleWithChauffeur({
                    benefitId: vehicleWithChauffeur.benefit.id,
                    fields: selectedFields.flatMap((fields) =>
                      fields.split(';'),
                    ),
                  });
                navigate(
                  PATHS.home.benefits.vehicleWithChauffeur.view.replace(
                    ':benefitId',
                    duplicatedVehicleWithChauffeur.benefitid,
                  ),
                  { state: location.state },
                );
              }
            }}
            closeModal={() => {
              setIsDuplicationModalOpen(false);
            }}
            fields={getTranslatedConst(
              VEHICLE_WITH_CHAUFFEUR_DUPLICATION_FIELDS,
            )}
            type="prestation"
            code={vehicleWithChauffeur.benefit.code}
          />
        )}
      </StyledContainer>
    </FormProvider>
  );
};

export default ViewVehicleWithChauffeurForm;
